import type {
  AppFixedInfo,
  AppVersion,
  DisplayInfo,
  ListingCategory,
  Localization,
  MainPackageInfo,
  Runtime,
  Platform,
  PlatformPackage,
  PlatformPackageParseStatus,
  EventDiff,
  EventDiffItem,
  PermissionDiff,
  PermissionDiffItem,
  SupportAccount,
  VersionOperator,
  VersionStatus,
} from '@/types'

function resolveAvailableType(
  available?: {
    type?: string
    members?: string[]
    departments?: string[]
  },
): AppFixedInfo['available_type'] {
  if (available?.type === 'all' || available?.type === 'specified') {
    return available.type
  }
  const hasSpecified =
    (available?.members?.length ?? 0) > 0 ||
    (available?.departments?.length ?? 0) > 0
  return hasSpecified ? 'specified' : 'all'
}

function resolveAvailableTypeFromFixed(
  fixed: AppFixedInfo,
): 'all' | 'specified' {
  if (fixed.available_type === 'all' || fixed.available_type === 'specified') {
    return fixed.available_type
  }
  return fixed.eligible_members.length > 0 ||
    fixed.eligible_departments.length > 0
    ? 'specified'
    : 'all'
}

/**
 * 只认清单 key：win32-<arch> / darwin-<arch>，原样保留、不按 OS 合并
 * （darwin-arm64 与 darwin-x64 是两个槽）。linux-* / ios / android / 家族名一律丢弃。
 * 须与后端同窗口上线：子包字段只认清单 key；列表 ?platform= / 货架结果仍是家族名。
 */
const PLATFORM_KEY_RE = /^(win32|darwin)-[a-z0-9_]+$/

function rawPlatformToken(value: unknown): string | undefined {
  const raw =
    typeof value === 'string'
      ? value
      : value && typeof value === 'object'
        ? ((value as { platform?: unknown; type?: unknown }).platform ??
          (value as { type?: unknown }).type)
        : undefined
  if (typeof raw !== 'string') return undefined
  const token = raw.trim().toLowerCase()
  return token || undefined
}

/** 接口里的平台可能是字符串，也可能是 { platform } / { type } */
export function normalizePlatform(value: unknown): Platform | undefined {
  const token = rawPlatformToken(value)
  return token && PLATFORM_KEY_RE.test(token) ? (token as Platform) : undefined
}

/** 非数组返回 undefined，便于上层区分「没给」与「给了空数组」 */
export function normalizePlatforms(value: unknown): Platform[] | undefined {
  if (!Array.isArray(value)) return undefined
  const out: Platform[] = []
  const dropped: string[] = []
  for (const item of value) {
    const token = rawPlatformToken(item)
    if (!token) continue
    if (PLATFORM_KEY_RE.test(token)) {
      const p = token as Platform
      if (!out.includes(p)) out.push(p)
    } else {
      dropped.push(token)
    }
  }
  // 联调期发现前后端平台键未对齐（仍回 windows/macos 或光杆 darwin）
  if (dropped.length > 0) {
    console.warn(
      `[platform] dropped non-dist keys (expected win32-* / darwin-*): ${dropped.join(', ')}`,
    )
  }
  return out
}

const RUNTIME_VALUES = new Set<Runtime>(['desktop', 'web'])

/** 只保留 desktop / web；空数组或全是未知值时视为未声明 */
export function normalizeRuntimes(raw: unknown): Runtime[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: Runtime[] = []
  for (const item of raw) {
    if (typeof item !== 'string' || !RUNTIME_VALUES.has(item as Runtime)) continue
    const runtime = item as Runtime
    if (!out.includes(runtime)) out.push(runtime)
  }
  return out.length ? out : undefined
}

/** API 扁平版本详情 DTO（对齐 api (2).md，字段一律 snake_case） */
export interface VersionDetailDto {
  version_id?: string
  version_code?: string
  status?: VersionStatus
  platforms?: Platform[]
  uploaded_platforms?: unknown
  released_at?: string | null
  last_operated_at?: string
  scheduled_at?: string | null
  operator?: VersionOperator
  is_first_version?: boolean
  review_comment?: string | null
  distribution_type?: AppFixedInfo['distribution_type']
  distribution_locked?: boolean
  listing_space_types?: ListingCategory[]
  space?: { id?: string; name?: string; icon_url?: string }
  available_members?: {
    type?: string
    departments?: string[]
    members?: string[]
  }
  supported_languages?: string[]
  default_language?: string
  categories?: string[]
  tags?: string[]
  website_url?: string
  privacy_policy_url?: string
  terms_url?: string
  support_account?: {
    username?: string
    nickname?: string
    avatar_url?: string
    space_name?: string
  } | null
  localizations?: Record<
    string,
    {
      tagline?: string
      description?: string
      pc_screenshots?: Array<{ url: string; title?: string } | string>
      mobile_screenshots?: Array<{ url: string; title?: string } | string>
    }
  >
  changelog?: Record<string, string>
  main_package?: {
    upload_id?: string
    file_name?: string
    file_size?: number
    main_package_url?: string
    icon_url?: string
    uploaded_at?: string
    /** 版本详情 / 上传草稿 / parse-result 都会带；缺失时退回按声明平台推断 */
    needs_platform_packages?: boolean
    /** 清单 runtimes：desktop / web。详情 main_package.runtimes */
    runtimes?: Runtime[]
  }
  platform_packages?: Array<{
    platform: Platform
    upload_id?: string
    file_id?: string
    package_url?: string
    package_version?: string
    file_name?: string
    file_size?: number
    is_historical?: boolean
    from_version_id?: string
    parse_status?: string
  }>
  scope_snapshot?: AppVersion['scope_snapshot']
  /** 详情原样；camelCase 在 mapPermissionDiff 里收 */
  permission_diff?: unknown
  /** 详情原样；camelCase 在 mapEventDiff 里收 */
  event_diff?: unknown
  app_id?: string
}

function bytesToSizeMB(bytes?: number): number {
  if (!bytes || bytes <= 0) return 1
  return Math.max(1, Math.round(bytes / 1024 / 1024))
}

function mapScreenshotUrls(
  items?: Array<{ url: string; title?: string } | string>,
): string[] {
  if (!items) return []
  return items.map((x) => (typeof x === 'string' ? x : x.url))
}

function mapLocalization(
  raw:
    | {
        tagline?: string
        description?: string
        pc_screenshots?: Array<{ url: string; title?: string } | string>
        mobile_screenshots?: Array<{ url: string; title?: string } | string>
      }
    | null
    | undefined,
): Localization {
  const r = raw ?? {}
  return {
    tagline: r.tagline ?? '',
    description: r.description ?? '',
    pc_screenshots: mapScreenshotUrls(r.pc_screenshots),
    mobile_screenshots: mapScreenshotUrls(r.mobile_screenshots),
  }
}

function mapSupportAccount(
  raw: NonNullable<VersionDetailDto['support_account']>,
): SupportAccount {
  return {
    username: (raw.username ?? '').trim().replace(/^@+/, ''),
    nickname: raw.nickname ?? '',
    avatar_url: raw.avatar_url ?? '',
    space_name: raw.space_name,
  }
}

function mapMainPackage(
  dto: VersionDetailDto,
  platforms: Platform[],
): MainPackageInfo | undefined {
  const mp = dto.main_package
  if (!mp?.file_name && !dto.version_code) return undefined
  return {
    file_name: mp?.file_name ?? 'package.tgz',
    size_mb: bytesToSizeMB(mp?.file_size),
    version_code: dto.version_code ?? '0.0.0',
    icon: mp?.icon_url,
    platforms,
    needs_platform_packages: mp?.needs_platform_packages ?? platforms.length > 0,
    runtimes: normalizeRuntimes(mp?.runtimes),
  }
}

function mapPlatformPackages(
  items: VersionDetailDto['platform_packages'],
): PlatformPackage[] | undefined {
  if (!items) return undefined
  return items.flatMap((p) => {
    const platform = normalizePlatform(p.platform)
    if (!platform) return []
    return {
      platform,
      file_name: p.file_name,
      size_mb: p.file_size != null ? bytesToSizeMB(p.file_size) : undefined,
      package_version: p.package_version ?? '',
      is_historical: !!p.is_historical,
      upload_id: p.upload_id,
      file_id: p.file_id,
      package_url: p.package_url,
      from_version_id: p.from_version_id,
      parse_status: normalizeParseStatus(p.parse_status),
    }
  })
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return undefined
}

function mapTokenType(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function mapDiffItems(raw: unknown): PermissionDiffItem[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const row = asRecord(item)
    if (!row) return []
    const scope = String(row.scope ?? '').trim()
    if (!scope) return []
    const name = String(row.name ?? '').trim()
    const sensitivity = String(row.sensitivity ?? '').trim()
    const status = String(row.status ?? '').trim()
    const token_type = mapTokenType(row.token_type ?? row.tokenType)
    return [
      {
        scope,
        ...(name ? { name } : {}),
        ...(sensitivity ? { sensitivity } : {}),
        ...(status ? { status } : {}),
        ...(token_type !== undefined ? { token_type } : {}),
      },
    ]
  })
}

/** 详情 permission_diff → 领域对象。缺字段时不返回，避免把列表里的旧值盖掉 */
export function mapPermissionDiff(raw: unknown): PermissionDiff | undefined {
  const row = asRecord(raw)
  if (!row) return undefined
  const added = mapDiffItems(row.added)
  const removed = mapDiffItems(row.removed)
  const unchanged_items = mapDiffItems(row.unchanged_items ?? row.unchangedItems)
  const unchanged =
    typeof row.unchanged === 'boolean'
      ? row.unchanged
      : added.length === 0 && removed.length === 0
  return { unchanged, added, removed, unchanged_items }
}

function mapSubscribeType(raw: unknown): EventDiffItem['subscribe_type'] | undefined {
  if (raw === 'tenant' || raw === 'user') return raw
  if (raw === 1 || raw === '1') return 'tenant'
  if (raw === 2 || raw === '2') return 'user'
  return undefined
}

function mapEventDiffItems(raw: unknown): EventDiffItem[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const row = asRecord(item)
    if (!row) return []
    const event_code = String(
      row.event_code ?? row.eventCode ?? '',
    ).trim()
    if (!event_code) return []
    const name = String(row.name ?? '').trim()
    const subscribe_type = mapSubscribeType(
      row.subscribe_type ?? row.subscribeType,
    )
    return [
      {
        event_code,
        ...(name ? { name } : {}),
        ...(subscribe_type ? { subscribe_type } : {}),
      },
    ]
  })
}

/** 详情 event_diff → 领域对象。缺字段时不返回，避免把列表里的旧值盖掉 */
export function mapEventDiff(raw: unknown): EventDiff | undefined {
  const row = asRecord(raw)
  if (!row) return undefined
  const added = mapEventDiffItems(row.added)
  const removed = mapEventDiffItems(row.removed)
  const unchanged_items = mapEventDiffItems(
    row.unchanged_items ?? row.unchangedItems,
  )
  const unchanged =
    typeof row.unchanged === 'boolean'
      ? row.unchanged
      : added.length === 0 && removed.length === 0
  return { unchanged, added, removed, unchanged_items }
}

export function normalizeParseStatus(
  raw: unknown,
): PlatformPackageParseStatus | undefined {
  return raw === 'pending' || raw === 'success' || raw === 'failed'
    ? raw
    : undefined
}

/** 扁平详情 → 嵌套领域 AppVersion */
export function mapVersionDetail(dto: VersionDetailDto): AppVersion {
  const platforms =
    normalizePlatforms(dto.platforms) ??
    normalizePlatforms(dto.platform_packages?.map((p) => p.platform)) ??
    []

  const app_fixed_info: AppFixedInfo | undefined =
    dto.distribution_type != null ||
    dto.listing_space_types != null ||
    dto.space != null ||
    dto.available_members != null ||
    dto.distribution_locked != null
      ? {
          distribution_type: dto.distribution_type ?? 'self_built',
          listing_space_types: dto.listing_space_types ?? [],
          owner_workspace: dto.space?.name,
          owner_space_id: dto.space?.id,
          available_type: resolveAvailableType(dto.available_members),
          eligible_members: dto.available_members?.members ?? [],
          eligible_departments: dto.available_members?.departments ?? [],
          distribution_locked: dto.distribution_locked,
        }
      : undefined

  const hasDisplay =
    dto.supported_languages != null ||
    dto.localizations != null ||
    dto.categories != null ||
    dto.tags != null ||
    dto.website_url != null ||
    dto.privacy_policy_url != null ||
    dto.terms_url != null ||
    dto.support_account != null ||
    dto.default_language != null

  const display_info: DisplayInfo | undefined = hasDisplay
    ? {
        supported_languages: dto.supported_languages ?? ['en'],
        default_language: dto.default_language ?? 'en',
        categories: dto.categories ?? [],
        tags: dto.tags ?? [],
        website_url: dto.website_url,
        privacy_policy_url: dto.privacy_policy_url,
        terms_url: dto.terms_url,
        support_account: dto.support_account
          ? mapSupportAccount(dto.support_account)
          : undefined,
        localizations: Object.fromEntries(
          Object.entries(dto.localizations ?? {}).map(([lang, loc]) => [
            lang,
            mapLocalization(loc),
          ]),
        ),
      }
      : undefined

  const permission_diff = mapPermissionDiff(dto.permission_diff)
  const event_diff = mapEventDiff(dto.event_diff)

  return {
    id: dto.version_id ?? '',
    version: dto.version_code ?? '0.0.0',
    status: dto.status ?? 'developing',
    platforms,
    uploaded_platforms: (() => {
      const ups = normalizePlatforms(dto.uploaded_platforms)
      return ups?.length ? ups : undefined
    })(),
    released_at: dto.released_at ?? null,
    last_operated_at: dto.last_operated_at ?? new Date().toISOString(),
    scheduled_at: dto.scheduled_at ?? null,
    operator: dto.operator,
    is_first_version: dto.is_first_version,
    review_comment: dto.review_comment ?? undefined,
    changelog: dto.changelog,
    main_package: mapMainPackage(dto, platforms),
    platform_packages: mapPlatformPackages(dto.platform_packages),
    app_fixed_info,
    display_info,
    scope_snapshot: dto.scope_snapshot,
    ...(permission_diff ? { permission_diff } : {}),
    ...(event_diff ? { event_diff } : {}),
  }
}

/** 嵌套领域 patch → 扁平 PATCH body（字段名对齐接口文档 snake_case） */
export function toSaveDraftBody(
  patch: Partial<AppVersion>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {}

  if (patch.changelog !== undefined) {
    const changelog = Object.fromEntries(
      Object.entries(patch.changelog).filter(
        ([, text]) => typeof text === 'string' && text.trim(),
      ),
    )
    if (Object.keys(changelog).length > 0) {
      body.changelog = changelog
    }
  }
  if (patch.scheduled_at !== undefined) body.scheduled_at = patch.scheduled_at

  const fixed = patch.app_fixed_info
  if (fixed) {
    body.distribution_type = fixed.distribution_type
    if (fixed.distribution_type === 'self_built') {
      // 定向分发：PATCH 必须带 space.id，否则 submit 报 32305
      const spaceId = fixed.owner_space_id?.trim()
      if (spaceId) {
        body.space = {
          id: spaceId,
          ...(fixed.owner_workspace?.trim()
            ? { name: fixed.owner_workspace.trim() }
            : {}),
        }
      }
      body.listing_space_types = []
    } else {
      body.listing_space_types = fixed.listing_space_types
      const type = resolveAvailableTypeFromFixed(fixed)
      body.available_members = {
        type,
        members: type === 'all' ? [] : fixed.eligible_members,
        departments: type === 'all' ? [] : fixed.eligible_departments,
      }
    }
  }

  const display = patch.display_info
  if (display) {
    body.supported_languages = display.supported_languages
    body.default_language = display.default_language
    body.categories = display.categories
    body.tags = display.tags
    if (display.website_url !== undefined) body.website_url = display.website_url
    if (display.privacy_policy_url !== undefined)
      body.privacy_policy_url = display.privacy_policy_url
    if (display.terms_url !== undefined) body.terms_url = display.terms_url
    const supportUsername = display.support_account?.username
      .trim()
      .replace(/^@+/, '')
    body.support_account = supportUsername ? { username: supportUsername } : null
    body.localizations = Object.fromEntries(
      Object.entries(display.localizations).map(([lang, loc]) => [
        lang,
        {
          tagline: loc.tagline,
          description: loc.description,
          pc_screenshots: loc.pc_screenshots.map((url) => ({ url })),
          mobile_screenshots: loc.mobile_screenshots.map((url) => ({ url })),
        },
      ]),
    )
  }

  return body
}
